/**
 * Interactive Plate Preview Component
 * 
 * Renders a realistic license plate with inline input zones.
 * Input happens directly inside the plate preview.
 */

import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  type PlateSize,
  type PlateInput,
  type PlateType,
  type VehicleCategory,
  type PlateRule,
  parseRawInput,
} from '@/services/rules/plateRulesEngine'
import { filterCityCodes } from '@/data/germanCityCodes'

interface InteractivePlatePreviewProps {
  plateSize: PlateSize
  plateInput: PlateInput
  plateType: PlateType
  vehicleCategory: VehicleCategory
  rule: PlateRule
  onInputChange: (input: PlateInput) => void
  onReserve?: (plate: PlateInput) => void
}

type InputZone = 'cityCode' | 'letters' | 'numbers' | null

const InteractivePlatePreview = ({
  plateSize,
  plateInput,
  plateType,
  vehicleCategory,
  rule,
  onInputChange,
  onReserve,
}: InteractivePlatePreviewProps) => {
  const [activeZone, setActiveZone] = useState<InputZone>('cityCode')
  const [showCursor, setShowCursor] = useState(true)
  const [cityCodeSuggestions, setCityCodeSuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [validationMessage, setValidationMessage] = useState<string | null>(null)

  // Responsive Scaling
  const [scale, setScale] = useState(1)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const cityCodeInputRef = useRef<HTMLInputElement>(null)
  const lettersInputRef = useRef<HTMLInputElement>(null)
  const numbersInputRef = useRef<HTMLInputElement>(null)

  // Calculate aspect ratio and scale
  const aspectRatio = plateSize.width / plateSize.height
  const isTwoLine = plateSize.layout === 'zweizeilig'
  const isMotorcycle = vehicleCategory === 'MOTORCYCLE' || vehicleCategory === 'LEICHTKRAFT'
  const isTraktor = vehicleCategory === 'TRAKTOR'
  const isMotoExact = vehicleCategory === 'MOTORCYCLE'
  const isLeichtkraft = vehicleCategory === 'LEICHTKRAFT'

  // Determine base dimensions for preview
  let baseWidth = 300
  let baseHeight = 300 / aspectRatio

  if (isMotorcycle || isTraktor) {
    baseHeight = 400
    baseWidth = baseHeight * aspectRatio
  } else if (isTwoLine) {
    baseWidth = 400
    baseHeight = baseWidth / aspectRatio
  } else {
    baseWidth = 500
    baseHeight = baseWidth / aspectRatio
  }

  // Responsive Observer
  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width
        if (width && baseWidth) {
          // Scale down if container is smaller than plate, but don't scale up (max 1)
          // Actually, visually scaling up is okay if needed, but usually we restrict width.
          // Let's cap at 1.0 to stay sharp, unless we want to fill? 1.0 is standard.
          setScale(Math.min(1, width / baseWidth))
        }
      }
    })

    observer.observe(el)
    return () => observer.disconnect()
  }, [baseWidth])

  // Blinking cursor animation
  useEffect(() => {
    const interval = setInterval(() => {
      setShowCursor(prev => !prev)
    }, 530)
    return () => clearInterval(interval)
  }, [])

  // Auto-focus on city code when component mounts
  useEffect(() => {
    if (!plateInput.cityCode && cityCodeInputRef.current) {
      setTimeout(() => {
        cityCodeInputRef.current?.focus()
        setActiveZone('cityCode')
      }, 100)
    }
  }, [])

  // Auto-advance cursor between zones
  useEffect(() => {
    if (activeZone === 'cityCode') {
      const cityCode = plateInput.cityCode || ''
      if (cityCode.length === rule.maxCityCodeLength && !plateInput.letters) {
        // Move to letters after max city code length
        setTimeout(() => {
          setActiveZone('letters')
          lettersInputRef.current?.focus()
        }, 200)
      }
    } else if (activeZone === 'letters') {
      const letters = plateInput.letters || ''
      const cityCodeLength = (plateInput.cityCode || '').length

      // Get allowed letter count based on city code length
      const allowedCombinations = rule.cityCodeDependentCombinations
        ? (cityCodeLength === 1 ? rule.cityCodeDependentCombinations.cityCodeLength1 :
          cityCodeLength === 2 ? rule.cityCodeDependentCombinations.cityCodeLength2 :
            rule.cityCodeDependentCombinations.cityCodeLength3)
        : rule.allowedCombinations

      const maxLetters = allowedCombinations?.reduce((max, combo) =>
        Math.max(max, combo.letters), 0) || 2

      if (letters.length >= maxLetters && !plateInput.numbers) {
        // Move to numbers after max letters
        setTimeout(() => {
          setActiveZone('numbers')
          numbersInputRef.current?.focus()
        }, 200)
      }
    }
  }, [plateInput, activeZone, rule])

  // Suffix Logic (E, H, Saison)
  // Moved up to be used in validation and trimming logic
  const isSaison = plateType.includes('SEASON')
  const isE = plateType.startsWith('E')
  const isH = plateType.startsWith('H')

  // If E or H, we have 1 less letter available for the user input
  const effectiveMaxLetters = (isE || isH) ? 1 : 2

  // STATE SEPARATION: Raw Input (User typing) vs Parsed/Prop Input (Valid Plate)
  // We initialize raw state from props once, then sync selectively
  const [rawInput, setRawInput] = useState<PlateInput>({
    cityCode: plateInput.cityCode || '',
    letters: plateInput.letters || '',
    numbers: plateInput.numbers || '',
    saisonStart: plateInput.saisonStart || '04',
    saisonEnd: plateInput.saisonEnd || '10',
  })

  // Sanitize / Parse Helpers REMOVED - using plateRulesEngine instead

  // SYNC: Prop -> Raw (Only if prop differs from what our current raw would produce)
  // This prevents resetting "Invalid" characters while typing, but allows updating if Parent changes (e.g. Search Result click)
  useEffect(() => {
    // If prop changes externally (length diff or value diff), update raw
    // We trust prop is "formatted", so we just set raw to prop if different
    if (plateInput.cityCode !== rawInput.cityCode) {
      // Only update if prop is significantly different? 
      // Actually, if we type '1', prop doesn't change (invalid).
      // If prop changes, it's external.
      // But wait, onInputChange feeds prop back.
      // If we type '1', onInputChange sends '' (formatted). Prop comes back ''.
      // If we set raw to '' from prop, we lose '1'.
      // We must only update raw from prop if the prop is NOT what we just sent.
      // But we don't know what we just sent easily here without ref.
      // BETTER: Only sync if raw is empty? Or use a ref to track "local update in progress"?
    }
    // Actually, controlled input pattern usually relies on value prop.
    // Here we split state.
    // If the PARENT drives the state, we must sync.
    // The previous logic was:
    // const rawParsed = parseCity(raw); if (rawParsed !== prop) setRaw(prop);
    // This meant: if our raw leads to this prop, don't overwrite raw.
    // New logic using parseRawInput:
    const { formatted } = parseRawInput(rawInput, rule)
    if (formatted.cityCode !== plateInput.cityCode) {
      setRawInput(prev => ({ ...prev, cityCode: plateInput.cityCode || '' }))
    }
  }, [plateInput.cityCode])

  useEffect(() => {
    const { formatted } = parseRawInput(rawInput, rule)
    if (formatted.letters !== plateInput.letters) {
      setRawInput(prev => ({ ...prev, letters: plateInput.letters || '' }))
    }
  }, [plateInput.letters])

  useEffect(() => {
    const { formatted } = parseRawInput(rawInput, rule)
    if (formatted.numbers !== plateInput.numbers) {
      setRawInput(prev => ({ ...prev, numbers: plateInput.numbers || '' }))
    }
  }, [plateInput.numbers])

  // EFFECT: Auto-trim letters if plate type changes and we exceed new limit
  useEffect(() => {
    if (rawInput.letters && rawInput.letters.length > effectiveMaxLetters) {
      const trimmed = rawInput.letters.slice(0, effectiveMaxLetters)
      setRawInput(prev => ({ ...prev, letters: trimmed }))

      // Propagate change
      const { formatted } = parseRawInput({ ...rawInput, letters: trimmed }, rule)
      onInputChange(formatted)
    }
  }, [plateType, effectiveMaxLetters, rawInput.letters])

  // --- HANDLERS (Update Raw -> Propagate Parsed) ---

  const handleCityRawChange = (value: string) => {
    // 1. Update RAW (Allows anything, just Uppercase for visual consistency)
    // User requested "Return exactly what user typed", but plates are uppercase.
    // We will allow INVALID chars, but uppercase valid ones?
    // Let's just Upper it.
    const rawVal = value.toUpperCase()
    setRawInput(prev => ({ ...prev, cityCode: rawVal }))

    // 2. Parse & Propagate
    // Strict Length Limit: maxCityCodeLength
    const truncatedVal = rawVal.slice(0, rule.maxCityCodeLength)
    setRawInput(prev => ({ ...prev, cityCode: truncatedVal }))

    const { formatted, validation } = parseRawInput({ ...rawInput, cityCode: truncatedVal }, rule)
    onInputChange(formatted)

    // 3. Validation (Informational)
    const upper = truncatedVal.toUpperCase()

    // Suggestion logic: Show suggestions if match found and not full length yet
    // Or always show if partial match?
    // User wants "Show dropdown suggestions matching prefix"
    if (!upper.includes('?') && upper.length > 0) {
      const suggestions = filterCityCodes(upper)
      setCityCodeSuggestions(suggestions)
      setShowSuggestions(suggestions.length > 0)

      // Auto-select if perfect match and length >= 3 (or user hit max)? 
      // Or just let user keep typing? User said "On selection... Set city... Focus next".
      // We do that in handleSuggestionClick.

      // Auto-advance if max length reached and it is a valid city code?
      // But we might have multiple valid codes (e.g. B, BA, BAR).
      // If I type B, suggestion B, BA, BAR, BI...
      // If I type BA, suggestion BA, BAR, BAM...
      // If I type BAR, suggestion BAR...
      // If I type B and wait? No, keep focus.

      // Strict Auto-advance ONLY if max length reached (3) OR (length 1/2 and exact match is unique/confirmed?)
      // Safer to just wait for 3 chars or Enter or Click.
      if (upper.length >= rule.maxCityCodeLength) {
        // Maybe focus next?
        // But what if I meant a 2-letter code and typed 3?
        // Let's stick to rule.maxCityCodeLength.
        // Actually, if I type a valid 1-letter code like 'B', I want to move on.
        // But 'B' is valid, 'BA' is valid.
        // So I can't auto-advance on 'B'.
        // I can only auto-advance if I hit max length (3).
        if (upper.length === rule.maxCityCodeLength) {
          setTimeout(() => {
            setActiveZone('letters')
            lettersInputRef.current?.focus()
            setShowSuggestions(false)
          }, 200)
        }
      }
    } else {
      setShowSuggestions(false)
    }

    const isSearch = rawVal.includes('?')
    if (!validation.valid && !isSearch) {
      // Warning but don't block
      setValidationMessage(validation.error || null)
    } else {
      setValidationMessage(null)
    }
  }

  const handleLettersRawChange = (value: string) => {
    // Regex: Allow 0-2 chars, A-Z ÄÖÜ
    // We allow lowercase in raw, will be uppered.
    if (!/^[a-zA-ZäöüÄÖÜ?]*$/.test(value)) return // Block invalid chars immediately? Or allow and warn? 
    // User said: "Regex: /^[A-ZÄÖÜ]{0,2}$/ ... Do NOT block partial input"
    // So we block invalid characters (numbers, symbols), but allow length 0-2.
    const rawVal = value.toUpperCase()
    // STRICT LIMIT: Use effectiveMaxLetters
    const truncatedVal = rawVal.slice(0, effectiveMaxLetters)

    setRawInput(prev => ({ ...prev, letters: truncatedVal }))

    const { formatted, validation } = parseRawInput({ ...rawInput, letters: truncatedVal }, rule)
    onInputChange(formatted)

    const isSearch = truncatedVal.includes('?')
    if (!validation.valid && !isSearch) {
      setValidationMessage(validation.error || null)
      setTimeout(() => setValidationMessage(null), 3000)
    } else {
      setValidationMessage(null)
    }

    if (truncatedVal.length === effectiveMaxLetters && validation.valid) {
      // Auto-advance
      setTimeout(() => {
        setActiveZone('numbers')
        numbersInputRef.current?.focus()
      }, 100)
    }
  }

  const handleNumbersRawChange = (value: string) => {
    // Regex: 0-4 digits
    if (!/^[0-9?]*$/.test(value)) return
    const rawVal = value
    // STRICT LIMIT: Max 4 digits
    const truncatedVal = rawVal.slice(0, 4)

    setRawInput(prev => ({ ...prev, numbers: truncatedVal }))

    const { formatted, validation } = parseRawInput({ ...rawInput, numbers: truncatedVal }, rule)
    onInputChange(formatted)

    const isSearch = truncatedVal.includes('?')
    if (!validation.valid && !isSearch) {
      setValidationMessage(validation.error || null)
      setTimeout(() => setValidationMessage(null), 3000)
    } else {
      setValidationMessage(null)
    }
  }

  // Backspace Navigation:
  const handleLastNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, fieldName: 'letters' | 'numbers') => {
    if (e.key === 'Backspace' && e.currentTarget.value === '') {
      if (fieldName === 'numbers') {
        e.preventDefault()
        setActiveZone('letters')
        lettersInputRef.current?.focus()
      } else if (fieldName === 'letters') {
        e.preventDefault()
        setActiveZone('cityCode')
        cityCodeInputRef.current?.focus()
      }
    }
  }

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    // Treat suggestion as external update
    setRawInput(prev => ({ ...prev, cityCode: suggestion }))
    // Also propagate formatted, even though Raw is same as formatted here usually
    const { formatted } = parseRawInput({ ...rawInput, cityCode: suggestion }, rule)
    onInputChange(formatted)

    setShowSuggestions(false)
    setActiveZone('letters')
    setTimeout(() => lettersInputRef.current?.focus(), 100)
  }

  const renderSuffixContainer = () => {
    if (plateType === 'REGULAR') return null

    // Determine font size for suffix - slightly smaller than main text usually
    // but clearly readable.
    const suffixFontSize = `${parseFloat(fontSize) * 0.9}px`
    const saisonFontSize = `${parseFloat(fontSize) * 0.45}px`

    return (
      <div className={`flex items-center justify-center ${isTwoLine ? 'ml-0' : 'ml-2'} h-full text-black font-bold uppercase z-30`}>
        {/* E or H Marker */}
        {(isE || isH) && (
          <div className="flex items-center justify-center -mt-1" style={{ fontSize: suffixFontSize, lineHeight: 1 }}>
            {isE ? 'E' : 'H'}
          </div>
        )}

        {/* Saison Dates (Stacked) */}
        {isSaison && (
          <div className={`flex flex-col items-center justify-center ${(isE || isH) ? 'ml-1' : ''}`} style={{ lineHeight: 0.9 }}>
            {/* Start Month (Top) */}
            <input
              type="text"
              className="bg-transparent border-none text-center font-bold outline-none p-0 m-0 w-[1.5em]"
              style={{ fontSize: saisonFontSize, lineHeight: 1 }}
              value={rawInput.saisonStart}
              maxLength={2}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 2)
                setRawInput(prev => ({ ...prev, saisonStart: val }))
              }}
            />

            {/* Divider Line */}
            <div className="w-full h-[2px] bg-black my-[1px] opacity-80" />

            {/* End Month (Bottom) */}
            <input
              type="text"
              className="bg-transparent border-none text-center font-bold outline-none p-0 m-0 w-[1.5em]"
              style={{ fontSize: saisonFontSize, lineHeight: 1 }}
              value={rawInput.saisonEnd}
              maxLength={2}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 2)
                setRawInput(prev => ({ ...prev, saisonEnd: val }))
              }}
            />
          </div>
        )}
      </div>
    )
  }

  // Calculate scaling factor to account for suffix space
  // If we have a suffix, effectively reduce available width for main char scaling check?
  // Our scaling logic uses totalChars. Let's add suffix "weight" to totalChars for scaling purposes if needed.
  // E/H adds 1 char. Saison block is compact, maybe like 1.5 chars width.
  // Actually, suffix renders in a separate flex block, so main content shrinks if strictly flexed?
  // Let's assume standard plates just run out of space.
  const visualSuffixWeight = (isE || isH ? 1 : 0) + (isSaison ? 1 : 0)

  // VISUAL RENDER: Use PARSED values (from props, or derived from raw if needed? Props are safest for visual consistency)
  // Actually, prompts says "Plate preview uses parsedPlate".
  // props.plateInput IS the parsed plate (propagated up and back down).
  const cityCode = plateInput.cityCode || ''
  const letters = plateInput.letters || ''
  const numbers = plateInput.numbers || ''

  // RAW INPUTS for Fields
  const rawCity = rawInput.cityCode ?? ''
  const rawLetters = rawInput.letters ?? ''
  const rawNumbers = rawInput.numbers ?? ''

  // === RENDERING LOGIC MOVED FROM JSX ===
  // Calculate Pixel-to-MM scale
  const currentDomWidth = (isMotoExact || isTraktor) ? (400 * aspectRatio) : ((isTwoLine || isLeichtkraft) ? 400 : 500)
  const pxPerMm = currentDomWidth / plateSize.width

  // Dynamic Font Scaling Logic
  // We need to ensure text fits within the container without clipping.

  // Base font size targets (max possible size)
  const targetFontHeightMm = (isMotoExact || isLeichtkraft || isTraktor) ? 45 : 75
  let baseFontSizePx = targetFontHeightMm * pxPerMm

  // Scaling factors based on content length
  const totalChars = cityCode.length + letters.length + numbers.length + visualSuffixWeight

  let scaleFactor = 1.0

  if (!isTwoLine && !isMotorcycle) {
    // Single Line Scaling
    // Standard plate is ~8 chars max. 
    // If we have > 7 chars, we scale down.
    if (totalChars > 8) scaleFactor = 0.75
    else if (totalChars === 8) scaleFactor = 0.85
    else if (totalChars === 7) scaleFactor = 0.95
    else scaleFactor = 1.0
  } else {
    // Two Line Scaling
    // Line 1: City (usually 1-3 chars) -> rarely an issue, but check max width?
    // Line 2: Letters + Numbers (up to 4-5 chars)
    const line2Chars = letters.length + numbers.length
    if (line2Chars > 4) scaleFactor = 0.80
    else if (line2Chars === 4) scaleFactor = 0.90
    else scaleFactor = 1.0
  }

  const finalFontSizePx = baseFontSizePx * scaleFactor
  const fontSize = `${finalFontSizePx}px`

  // Input Style: Fluid width, centred, no overflow clipping
  const inputStyle = (value: string) => ({
    fontSize,
    width: value ? `${Math.max(value.length * 0.7, 1)}em` : '1em', // Fluid width estimate
    minWidth: '1em',
    maxWidth: '100%',
    lineHeight: 1.1, // Ensure no vertical clipping
    height: '1.2em', // Ensure bounding box fits letters
    textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
    padding: '0 2px',
    margin: 0
  })

  // Render Helpers
  const renderCityInput = () => (
    <div
      className="relative inline-flex items-center justify-center cursor-text"
      onClick={(e) => { e.stopPropagation(); setActiveZone('cityCode'); cityCodeInputRef.current?.focus() }}
    >
      <input
        ref={cityCodeInputRef}
        type="text"
        name="plate_city_code_custom"
        autoComplete="off"
        data-lpignore="true"
        value={rawCity}
        maxLength={rule.maxCityCodeLength}
        onChange={(e) => handleCityRawChange(e.target.value)}
        onFocus={() => setActiveZone('cityCode')}
        className="bg-transparent border-none outline-none font-bold text-black cursor-text text-center uppercase p-0 m-0"
        style={inputStyle(rawCity)}
      />
      {activeZone === 'cityCode' && showCursor && rawCity.length < (rule.maxCityCodeLength + 1) && (
        <span className="absolute flex items-center justify-start pointer-events-none" style={{ zIndex: 5, right: '-5px', top: '50%', transform: 'translateY(-50%)' }}>
          <span className="w-1 h-[80%] bg-black/50 animate-pulse" />
        </span>
      )}
    </div>
  )

  const renderSeals = () => (
    <div className={`flex flex-col items-center justify-center px-2 ${isTwoLine ? 'py-1' : ''} cursor-default`} onClick={(e) => e.stopPropagation()}>
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-200 to-gray-400 border border-gray-500 mb-1 shadow-sm flex items-center justify-center overflow-hidden">
        <div className="w-4 h-4 border border-gray-500 rounded-full opacity-50"></div>
      </div>
      <div className="w-8 h-8 rounded-full bg-orange-400 border border-orange-600 shadow-sm flex items-center justify-center rotate-12">
        <div className="text-[6px] text-black font-bold text-center leading-none">12<br />25</div>
      </div>
    </div>
  )

  const renderLettersNumbers = () => (
    <div className="flex items-center justify-center gap-1">
      {/* Letters */}
      <div
        className="relative inline-flex items-center justify-center cursor-text"
        onClick={(e) => { e.stopPropagation(); setActiveZone('letters'); lettersInputRef.current?.focus() }}
      >
        <input
          ref={lettersInputRef}
          type="text"
          name="plate_letters_code_custom"
          autoComplete="off"
          data-lpignore="true"
          value={rawLetters}
          maxLength={effectiveMaxLetters}
          onChange={(e) => handleLettersRawChange(e.target.value)}
          onKeyDown={(e) => handleLastNameKeyDown(e, 'letters')}
          onFocus={() => setActiveZone('letters')}
          className="bg-transparent border-none outline-none font-bold text-black cursor-text text-center uppercase p-0 m-0"
          style={inputStyle(rawLetters)}
        />
        {activeZone === 'letters' && showCursor && rawLetters.length < (rule.allowedCombinations?.reduce((max, c) => Math.max(max, c.letters), 0) || effectiveMaxLetters) && (
          <span className="absolute flex items-center justify-start pointer-events-none" style={{ zIndex: 5, right: '-5px', top: '50%', transform: 'translateY(-50%)' }}>
            <span className="w-1 h-[80%] bg-black/50 animate-pulse" />
          </span>
        )}
      </div>

      {/* Numbers */}
      <div
        className="relative inline-flex items-center justify-center cursor-text"
        onClick={(e) => { e.stopPropagation(); setActiveZone('numbers'); numbersInputRef.current?.focus() }}
      >
        <input
          ref={numbersInputRef}
          type="text"
          name="plate_numbers_code_custom"
          autoComplete="off"
          data-lpignore="true"
          value={rawNumbers}
          maxLength={4}
          onChange={(e) => handleNumbersRawChange(e.target.value)}
          onKeyDown={(e) => handleLastNameKeyDown(e, 'numbers')}
          onFocus={() => setActiveZone('numbers')}
          className="bg-transparent border-none outline-none font-bold text-black cursor-text text-center p-0 m-0"
          style={inputStyle(rawNumbers)}
        />
        {activeZone === 'numbers' && showCursor && rawNumbers.length < 5 && (
          <span className="absolute flex items-center justify-start pointer-events-none" style={{ zIndex: 5, right: '-5px', top: '50%', transform: 'translateY(-50%)' }}>
            <span className="w-1 h-[80%] bg-black/50 animate-pulse" />
          </span>
        )}
      </div>
    </div>
  )

  return (
    <div className="bg-dark-900 rounded-2xl p-6 border border-gray-800">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-300 mb-2">Vorschau</h3>
        <p className="text-xs text-gray-500">
          {plateSize.width} × {plateSize.height}
          {isTwoLine && ' (Zweizeilig)'}
        </p>
      </div>

      <div className="flex justify-center items-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-6 shadow-inner relative">
        {/* City Code Autocomplete Suggestions */}
        <AnimatePresence>
          {showSuggestions && cityCodeSuggestions.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full mb-2 bg-dark-800 border border-gray-700 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto"
              style={{ minWidth: '200px' }}
            >
              {cityCodeSuggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full px-4 py-2 text-left text-white hover:bg-primary-500/20 transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="w-full flex justify-center" ref={wrapperRef}>
          <div
            style={{
              width: baseWidth * scale,
              height: baseHeight * scale,
              position: 'relative',
              transition: 'width 0.1s, height 0.1s'
            }}
          >
            <div
              className="bg-white border-4 border-black rounded-lg shadow-lg overflow-hidden"
              style={{
                width: `${baseWidth}px`,
                height: `${baseHeight}px`,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                position: 'absolute',
                top: 0,
                left: 0,
                borderRadius: '4px',
              }}
            >
              {/* EU Blue Band (Left Side) */}
              <div
                className="absolute left-0 top-0 bottom-0 bg-blue-700 flex flex-col items-center justify-start pt-2 z-0"
                style={{ width: isMotorcycle ? '15%' : '10%' }}
              >
                {/* EU Stars Circle (Simplified) */}
                <div className="w-8 h-8 rounded-full border border-yellow-400 flex items-center justify-center mb-1 scale-75">
                  <span className="text-yellow-400 text-[10px]">★</span>
                </div>
                <span className="text-white font-bold text-xl">D</span>
              </div>

              {/* Main Plate Content */}
              <div
                className={`flex flex-1 items-center justify-center text-black font-mono z-10 pl-[10%] ${isTwoLine ? 'flex-col space-y-0' : 'flex-row'}`}
                style={{
                  height: '100%',
                  fontFamily: '"DIN 1451", "Engschrift", sans-serif',
                  gap: isTwoLine ? '0' : '4px'
                }}
              >
                {/* Hidden dummy input to prevent password manager autofill */}
                <input type="text" name="dummy_prevention_user" style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }} tabIndex={-1} />

                {/* Calculate Dynamic Font Size based on Real MM Dimensions */}
                {/* Calculate Dynamic Font Size used to be here - moved to top */}

                {(isTwoLine || isMotorcycle) ? (
                  <>
                    {/* Line 1: City Code */}
                    <div
                      className="h-[35%] w-full flex items-end justify-center pb-0 cursor-text hover:bg-black/5 rounded"
                      onClick={() => { setActiveZone('cityCode'); cityCodeInputRef.current?.focus() }}
                    >
                      {renderCityInput()}
                    </div>

                    {/* Middle: Seals */}
                    <div className="h-[25%] w-full flex items-center justify-center -my-1 z-10">
                      <div className="transform scale-90">
                        {renderSeals()}
                      </div>
                    </div>

                    {/* Line 2: Letters + Numbers */}
                    <div className="h-[40%] w-full flex items-start justify-center pt-0 relative z-20">
                      <div
                        className="flex items-center justify-center cursor-text hover:bg-black/5 rounded px-1 -mt-2"
                        onClick={() => {
                          if (activeZone === 'numbers') { numbersInputRef.current?.focus() }
                          else { setActiveZone('letters'); lettersInputRef.current?.focus() }
                        }}
                      >
                        {renderLettersNumbers()}
                      </div>


                      {/* E/H Indicator (Line 2) OR Saison Block */}
                      {plateType !== 'REGULAR' && (
                        <div className="absolute right-2 bottom-3 flex items-end">
                          {renderSuffixContainer()}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center cursor-text"
                    onClick={() => {
                      // Determine best focus target based on current state?
                      // Or simply focus City Code if empty?
                      if (!cityCode) cityCodeInputRef.current?.focus()
                      else if (!letters) lettersInputRef.current?.focus() // assuming we want letters next
                      else numbersInputRef.current?.focus()
                    }}
                  >
                    <div className="hover:bg-black/5 rounded px-1" onClick={(e) => { e.stopPropagation(); cityCodeInputRef.current?.focus() }}>
                      {renderCityInput()}
                    </div>
                    <div className="px-2">
                      {renderSeals()}
                    </div>
                    <div className="hover:bg-black/5 rounded px-1" onClick={(e) => { e.stopPropagation(); if (activeZone === 'numbers') numbersInputRef.current?.focus(); else lettersInputRef.current?.focus() }}>
                      {renderLettersNumbers()}
                    </div>

                    {/* Suffix Container (Flex Item) */}
                    {plateType !== 'REGULAR' && (
                      <div className="pl-1">
                        {renderSuffixContainer()}
                      </div>
                    )}

                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Validation Message */}
        {validationMessage && (
          <div className="mt-3 p-3 bg-red-500/10 border border-red-500/50 rounded-lg">
            <p className="text-red-400 text-sm text-center">{validationMessage}</p>
          </div>
        )}

        {/* Reserve Button */}
        {onReserve && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => onReserve && onReserve({ ...plateInput, saisonStart: rawInput.saisonStart, saisonEnd: rawInput.saisonEnd })}
              disabled={
                !plateInput.cityCode ||
                !plateInput.letters ||
                !plateInput.numbers ||
                rawLetters.length < 1 ||
                rawNumbers.length < 1 ||
                !!validationMessage
              }
              className={`
                w-full py-4 text-lg font-bold rounded-xl shadow-lg transition-all duration-300 transform
                ${(!plateInput.cityCode || !plateInput.letters || !plateInput.numbers || !!validationMessage)
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed opacity-50'
                  : 'bg-green-600 hover:bg-green-500 hover:scale-[1.02] hover:shadow-green-500/20 text-white shadow-green-900/50'
                }
              `}
            >
              Kennzeichen reservieren
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default InteractivePlatePreview
